import React from "react";

/**
 * Brand-palette notebook + location-pin SVG.
 *
 * Drawn inline instead of served as a PNG so:
 *   1. The notebook body tracks `currentColor` → one file, both themes.
 *   2. The pin accent stays a fixed teal (`#14b8a6`) — matches the teal
 *      dot on the `j` in the wordmark art.
 *
 * Aspect is square (80×80 viewBox). Sized via `className` from the parent.
 */
export const BrandIconGlyph = ({ className = "h-12 w-12", title = "Travel Gadjet" }) => (
  <svg
    viewBox="0 0 80 80"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    role="img"
    aria-label={title}
    data-testid="brand-icon-glyph"
  >
    <title>{title}</title>
    {/* Notebook body — rounded rectangle in currentColor. Slight right bias
        so the spiral binding on the left reads clearly. */}
    <rect x="18" y="6" width="56" height="68" rx="7" fill="currentColor" />
    {/* Spiral binding rings — thin pill shapes protruding from the spine */}
    <rect x="4" y="13" width="18" height="5" rx="2.5" fill="currentColor" />
    <rect x="4" y="26" width="18" height="5" rx="2.5" fill="currentColor" />
    <rect x="4" y="39" width="18" height="5" rx="2.5" fill="currentColor" />
    <rect x="4" y="52" width="18" height="5" rx="2.5" fill="currentColor" />
    <rect x="4" y="65" width="18" height="5" rx="2.5" fill="currentColor" />
    {/* Location pin — the ONLY teal accent on the notebook. Filled drop
        shape + hollow contrast-colored eye in the center. */}
    <path
      d="M46 14
         C38 14, 33 20, 33 27
         C33 36, 46 48, 46 48
         C46 48, 59 36, 59 27
         C59 20, 54 14, 46 14 Z"
      fill="#14b8a6"
    />
    {/* Eye of the pin + page lines — must contrast with the notebook body,
        so light mode = white (on black body), dark mode = black (on white
        body). Tailwind's fill utilities handle the theme swap inline. */}
    <circle cx="46" cy="26" r="5" className="fill-white dark:fill-black" />
    <rect x="30" y="56" width="34" height="4" rx="2" className="fill-white dark:fill-black" />
    <rect x="30" y="64" width="24" height="4" rx="2" className="fill-white dark:fill-black" />
  </svg>
);

/**
 * Canonical brand logo component.
 *
 * Four artwork files live under `/public/brand/`:
 *   - `logo-wordmark-light.png` — dark-blue wordmark on transparent bg, for
 *     LIGHT backgrounds.
 *   - `logo-wordmark-dark.png`  — white wordmark on transparent bg, for DARK
 *     backgrounds.
 *   - `icon-circle.png`         — teal circular app icon (iOS/Play store).
 *   - `icon-notebook-mono.png`  — monochrome black/white notebook glyph,
 *     safe to tint via CSS filters.
 *
 * Variants exposed:
 *   - `wordmark`: full brand lockup, auto-swaps light/dark via Tailwind
 *     `dark:hidden` / `dark:block` — no runtime theme detection needed.
 *   - `icon`: the circular app icon (teal bg + white notebook).
 *   - `icon-mono`: the monochrome notebook — picks up `currentColor`-ish
 *     tints via CSS filter when needed (rare).
 *   - `lockup`: transparent-bg splash lockup — inline notebook SVG on
 *     the left, wordmark on the right, tagline below. Used on the login
 *     page and anywhere we need the full brand ID without a panel.
 */
export const BrandLogo = ({
  variant = "wordmark",
  className = "",
  alt = "Travel Gadjet",
  showTagline = true,
  ...rest
}) => {
  if (variant === "icon") {
    return (
      <img
        src="/brand/icon-circle.png"
        alt={alt}
        className={className}
        data-testid="brand-icon-circle"
        {...rest}
      />
    );
  }
  if (variant === "icon-mono") {
    return (
      <img
        src="/brand/icon-notebook-mono.png"
        alt={alt}
        className={className}
        data-testid="brand-icon-mono"
        {...rest}
      />
    );
  }
  if (variant === "glyph") {
    // Raw inline SVG — black/white notebook body via currentColor, teal pin.
    return <BrandIconGlyph className={className} title={alt} />;
  }
  if (variant === "lockup") {
    // Full splash lockup: user-provided notebook icon (auto-swaps for
    // light/dark mode) + the wordmark PNG + tagline. The teal circle
    // App-Store icon is intentionally NOT used here — it lives only in
    // favicon/manifest/splash slots. Only the designer-shipped notebook
    // artwork appears on-screen.
    return (
      <div
        className={`flex flex-col items-center gap-3 ${className}`}
        data-testid="brand-lockup"
      >
        <div className="flex items-center gap-4">
          {/* Notebook icon — user-provided artwork. `icon-notebook-light.png`
              has a black body for light backgrounds; `icon-notebook-dark.png`
              has a white body for dark backgrounds. CSS swap, no JS. */}
          <img
            src="/brand/icon-notebook-light.png"
            alt={alt}
            className="block dark:hidden h-20 w-20 sm:h-24 sm:w-24 shrink-0"
            data-testid="brand-lockup-icon-light"
          />
          <img
            src="/brand/icon-notebook-dark.png"
            alt={alt}
            className="hidden dark:block h-20 w-20 sm:h-24 sm:w-24 shrink-0"
            data-testid="brand-lockup-icon-dark"
          />
          {/* Wordmark — user-provided PNG, auto-swaps light/dark via CSS. */}
          <img
            src="/brand/logo-wordmark-light.png"
            alt={alt}
            className="block dark:hidden h-16 sm:h-20 w-auto"
            data-testid="brand-lockup-wordmark-light"
          />
          <img
            src="/brand/logo-wordmark-dark.png"
            alt={alt}
            className="hidden dark:block h-16 sm:h-20 w-auto"
            data-testid="brand-lockup-wordmark-dark"
          />
        </div>
        {showTagline && (
          <p
            className="text-xl sm:text-2xl font-bold tracking-tight text-center leading-tight text-[#0F2A44] dark:text-white"
            style={{ fontFamily: "'Poppins', system-ui, -apple-system, sans-serif" }}
            data-testid="brand-lockup-tagline"
          >
            Plan, Track, and Manage
            <br />
            Your Adventures
          </p>
        )}
      </div>
    );
  }
  // Default: wordmark. Render both copies and let CSS pick based on theme.
  // Duplicates are <5KB each; negligible overhead vs a runtime useTheme check.
  return (
    <>
      <img
        src="/brand/logo-wordmark-light.png"
        alt={alt}
        className={`block dark:hidden ${className}`}
        data-testid="brand-wordmark-light"
        {...rest}
      />
      <img
        src="/brand/logo-wordmark-dark.png"
        alt={alt}
        className={`hidden dark:block ${className}`}
        data-testid="brand-wordmark-dark"
        {...rest}
      />
    </>
  );
};

export default BrandLogo;

